import React, { useEffect, useMemo, useRef } from 'react'
import { connect, useDispatch } from 'react-redux'

import { setProjectIdentity } from '@store/actionSlices/projectIdentity'
import { setWhiteLabel } from '@store/actionSlices/whiteLabel'
import { AssetHandlerType } from '@store/constants'
import {
  BackgroundType,
  ProjectIdentity,
  RootStateTypeExtra,
} from '@store/types'

import {
  selectFromResult as selectFromConfigResult,
  useGetConfigQuery,
} from '@api/config'
import { ShowcaseRoomConfig } from '@api/showcase-room'
import {
  selectFromWhiteLabelResult,
  useGetWhiteLabelsQuery,
} from '@api/white-label'

import AssetHandler from '@adUtilities/asset-handler/asset-handler'

type BackgroundStyle = {
  backgroundImage?: string
  backgroundColor?: string
}

export interface ContainerProps {
  className?: string
  children: React.ReactNode
  projectIdentity: ProjectIdentity
  showcaseRoomConfig: ShowcaseRoomConfig
  hasBottomNavigation?: boolean
  rootElementId?: string
  fullscreen?: boolean
}

function Container({
  className = '',
  children,
  projectIdentity,
  hasBottomNavigation,
  rootElementId,
  showcaseRoomConfig,
  fullscreen,
}: ContainerProps) {
  const dispatch = useDispatch()
  const containerRef = useRef<HTMLDivElement>(null)

  const {
    configData,
    isLoaded: isConfigLoaded,
    isError: configHasError,
  } = useGetConfigQuery(
    { projectName: projectIdentity.projectId },
    {
      selectFromResult: selectFromConfigResult,
      skip: !projectIdentity.projectId,
    }
  )

  const {
    whiteLabelData,
    isLoaded: isWhiteLabelLoaded,
    isError: whiteLabelHasError,
  } = useGetWhiteLabelsQuery(
    { projectName: projectIdentity.projectId },
    {
      selectFromResult: selectFromWhiteLabelResult,
      skip: !projectIdentity.projectId,
    }
  )

  const backgroundStyle = useMemo<BackgroundStyle>(() => {
    const remoteBackground = projectIdentity?.theme?.remoteBackground

    if (!remoteBackground) {
      return { backgroundColor: '#0F172A' }
    }

    const { type, url, color } = remoteBackground

    if (type === BackgroundType.Image && url) {
      return {
        backgroundImage: `url(${AssetHandler({ url, type: AssetHandlerType.NEW })})`,
      }
    }

    return { backgroundColor: color ?? '#0F172A' }
  }, [projectIdentity])

  const hasBackgroundImage = useMemo(
    () => Boolean(backgroundStyle.backgroundImage),
    [backgroundStyle]
  )

  useEffect(() => {
    const rootElem = document.getElementById(rootElementId || 'root')

    if (!rootElem) {
      return
    }

    const { backgroundImage, backgroundColor } = backgroundStyle

    rootElem.style.background = 'none'

    if (backgroundColor) {
      rootElem.style.backgroundColor = backgroundColor
    }
    if (backgroundImage) {
      rootElem.style.backgroundImage = backgroundImage
      rootElem.style.backgroundSize = 'cover'
    }

    if (containerRef.current) {
      containerRef.current.style.background = 'none'
    }
  }, [backgroundStyle, rootElementId])

  useEffect(() => {
    if (isConfigLoaded && !configHasError) {
      dispatch(
        setProjectIdentity({
          ...projectIdentity,
          ...configData,
          ...showcaseRoomConfig,
        })
      )
    }
  }, [isConfigLoaded, showcaseRoomConfig])

  useEffect(() => {
    if (isWhiteLabelLoaded && !whiteLabelHasError) {
      dispatch(setWhiteLabel(whiteLabelData))
    }
  }, [isWhiteLabelLoaded])

  return (
    <div
      className={`background-cover relative ml-auto h-screen transition-all duration-500 ease-in-out ${
        fullscreen || hasBottomNavigation ? 'w-screen' : 'w-[70%]'
      } ${className} ${
        hasBackgroundImage &&
        'overflow-hidden bg-black/[0.35] backdrop-blur-[75px]'
      }`}
      style={{ ...backgroundStyle }}
      ref={containerRef}
      id="container-root"
    >
      {children}
    </div>
  )
}

export default connect(
  ({
    projectIdentity,
    showcaseRoom: showcaseRoomConfig,
    appConfig,
  }: RootStateTypeExtra) => ({
    projectIdentity,
    showcaseRoomConfig,
    fullscreen: appConfig.fullscreen,
  })
)(Container)
